import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button as ShadcnButton } from '@/ui/shadcn/button';
import { FailureSurface } from './FailureSurface';
import { APP_NAME } from './RouteTitle';
import { reportError } from './telemetry';

interface Props {
    readonly children: ReactNode;
}

interface State {
    readonly failed: boolean;
    readonly message: string | undefined;
}

/**
 * The last-resort boundary. A render error anywhere below this point produces an honest failure
 * surface instead of the blank white page React gives you by default — and a blank page is the
 * worst outcome available, because the user cannot tell it from a broken network, a wrong URL, or
 * their data having disappeared.
 *
 * A class component because an error boundary has no hook equivalent; this is the one place in
 * the codebase where that is the right shape.
 */
export class AppErrorBoundary extends Component<Props, State> {
    override state: State = { failed: false, message: undefined };

    static getDerivedStateFromError(error: unknown): State {
        return { failed: true, message: error instanceof Error ? error.message : undefined };
    }

    override componentDidCatch(error: unknown, info: ErrorInfo): void {
        reportError({ error, where: info.componentStack ?? 'unknown' });
    }

    override render(): ReactNode {
        if (!this.state.failed) {
            return this.props.children;
        }
        // React adopts `index.html`'s `<title>` and removes it with the tree that owned it, so the
        // crash screen renders its own: without it the tab shows the bare URL (WCAG 2.4.2).
        return (
            <>
                <title>{APP_NAME}</title>
                <FailureSurface
                    what="this page"
                    detail={this.state.message}
                    action={
                        <ShadcnButton
                            onClick={() => {
                                window.location.reload();
                            }}
                        >
                            Reload the page
                        </ShadcnButton>
                    }
                />
            </>
        );
    }
}
